import type { RadioChangeEvent } from 'antd';
import { Button, Form, Modal, Radio } from 'antd';
import { useContext, useState } from 'react';

import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { AVAILABILITY_MODE } from 'app/typings/core/admin/experiences/enum/availabilityMode';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';

import { markFormDirty } from '../../../hook/useValidationStore';

const REQUIRED_RULE = [{ required: true, message: 'Required' }];

export const StockTypeField = () => {
  const { isViewMode } = useContext(ViewEditModeContext);
  const form = Form.useFormInstance();
  const { data: experienceCategories } = useExperiencesCategories();

  const categoryId = Form.useWatch<number>('categoryId', form);
  const availabilityMode = Form.useWatch<AVAILABILITY_MODE>(
    'availabilityMode',
    form
  );
  const showInInventory = Form.useWatch<boolean>('showInInventory', form);

  const isHiddenFromInventory = showInInventory === false;
  const selectedCategory = experienceCategories?.find(
    (category) => category.id === categoryId
  );
  const canUseTimeslots =
    selectedCategory?.allowTimeslots === true && !isHiddenFromInventory;
  const isTimeslot = availabilityMode === AVAILABILITY_MODE.TIMESLOT;

  const [showTimeslotModal, setShowTimeslotModal] = useState(false);

  const handleAvailabilityModeChange = (e: RadioChangeEvent) => {
    const newValue = e.target.value;

    if (
      newValue === AVAILABILITY_MODE.DAILY &&
      availabilityMode === AVAILABILITY_MODE.TIMESLOT
    ) {
      form.setFieldValue('availabilityMode', AVAILABILITY_MODE.TIMESLOT);
      setShowTimeslotModal(true);

      return;
    }

    markFormDirty();

    if (newValue === AVAILABILITY_MODE.TIMESLOT) {
      form.setFieldsValue({
        isTimeslotDaytimeOnly: false,
        openingConfiguration: {
          stockConfiguration: {
            type: OPENING_CONFIGURATION_TYPE.CONSTANT,
            configuration: { stock: null },
          },
        },
      });
    }
  };

  const handleTimeslotModalConfirm = () => {
    markFormDirty();
    form.setFieldValue('availabilityMode', AVAILABILITY_MODE.DAILY);
    setShowTimeslotModal(false);
  };

  const handleTimeslotModalCancel = () => {
    setShowTimeslotModal(false);
  };

  return (
    <>
      {canUseTimeslots && (
        <FormLineLayout
          isRequired
          label="Stock type"
          description={
            isTimeslot
              ? 'Timeslots are created and managed in the Hotel Extranet.'
              : undefined
          }
          hideDescriptionIcon
          disabled={isViewMode}
        >
          <Form.Item name="availabilityMode" rules={REQUIRED_RULE}>
            <Radio.Group
              disabled={isViewMode}
              onChange={handleAvailabilityModeChange}
            >
              <Radio value={AVAILABILITY_MODE.DAILY}>Daily stock</Radio>
              <Radio value={AVAILABILITY_MODE.TIMESLOT}>Timeslots</Radio>
            </Radio.Group>
          </Form.Item>
        </FormLineLayout>
      )}

      <Modal
        open={showTimeslotModal}
        onCancel={handleTimeslotModalCancel}
        footer={
          <>
            <Button onClick={handleTimeslotModalCancel}>Cancel</Button>
            <Button type="primary" onClick={handleTimeslotModalConfirm}>
              Yes, switch to daily stock
            </Button>
          </>
        }
        closable
        title="Disable timeslots"
      >
        Switching back to daily stock will delete the timeslots calendar for
        this experience. Are you sure you want to proceed?
      </Modal>
    </>
  );
};
